import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getUserDetails } from '../store/ui/selector';

export type VerificationPage = 'account' | 'cosign' | 'intro' | 'types' | 'confirmation';

const GUARANTOR_FLOW: VerificationPage[] = ['account', 'cosign', 'intro', 'types', 'confirmation'];
const APPLICANT_FLOW: VerificationPage[] = ['account', 'intro', 'types', 'confirmation'];

const pageFromPathname = (pathname: string): VerificationPage => {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (last === 'cosign' || last === 'intro' || last === 'types' || last === 'confirmation') {
    return last;
  }
  return 'account';
};

export const isGuarantorRole = (role?: string) => role === 'Guarantor' || role === 'Non-Resident Co-sign';

export function useVerificationNav() {
  const { propertyCode } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const userDetails = useSelector(getUserDetails);

  const isGuarantor = isGuarantorRole(userDetails?.role);
  const flow = isGuarantor ? GUARANTOR_FLOW : APPLICANT_FLOW;
  const currentPage = pageFromPathname(pathname);
  const currentIndex = Math.max(0, flow.indexOf(currentPage));
  const currentStep = currentIndex + 1;

  const goTo = (page: VerificationPage) => {
    const sub = page === 'account' ? '' : `/${page}`;
    navigate(`/${propertyCode}/verification${sub}${search}`);
  };

  const goNext = () => {
    const next = flow[Math.min(currentIndex + 1, flow.length - 1)];
    goTo(next);
  };

  const goBack = () => {
    const prev = flow[Math.max(currentIndex - 1, 0)];
    goTo(prev);
  };

  return { currentPage, currentStep, isGuarantor, goTo, goNext, goBack };
}
